import React, { useState, useEffect } from 'react';
import { Mail, User, MessageSquare, Phone } from 'lucide-react';
import axios from 'axios';
import { getSettingsSync } from '../utils/settings';
import './Demo.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [settings, setSettings] = useState(getSettingsSync());

  useEffect(() => {
    const loadSettings = async () => {
      const { getSettings } = await import('../utils/settings');
      const fetchedSettings = await getSettings();
      setSettings(fetchedSettings);
    };
    loadSettings();
    
    const handleSettingsUpdate = () => {
      loadSettings();
    };
    
    window.addEventListener('settingsUpdated', handleSettingsUpdate);
    return () => window.removeEventListener('settingsUpdated', handleSettingsUpdate);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API}/contact`, formData);
      console.log('Contact message submitted:', response.data);
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting contact message:', err);
      setError('Failed to submit message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="demo-page">
        <section className="success-section">
          <div className="success-content">
            <div className="success-icon">✓</div>
            <h1 className="display-large">Message Received!</h1>
            <p className="body-large" style={{ marginTop: '24px' }}>
              Thank you for contacting us. We'll get back to you within 24 hours.
            </p>
            <button 
              onClick={() => setSubmitted(false)} 
              className="btn-secondary"
              style={{ marginTop: '40px' }}
            >
              Send Another Message
            </button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="demo-page">
      <section className="page-hero">
        <h1 className="display-huge">Contact Us</h1>
        <p className="body-large" style={{ marginTop: '32px', maxWidth: '800px', margin: '32px auto 0' }}>
          Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
        </p>
        
        {/* Contact Information */}
        {settings.showContactInfo && (
          <div style={{ 
            marginTop: '48px', 
            display: 'flex', 
            gap: '40px', 
            justifyContent: 'center',
            flexWrap: 'wrap',
            fontSize: '18px'
          }}>
            <a 
              href={`mailto:${settings.contactEmail}`}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px',
                color: 'var(--brand-primary)',
                textDecoration: 'none',
                transition: 'opacity 0.3s'
              }}
              onMouseOver={(e) => e.currentTarget.style.opacity = '0.8'}
              onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
            >
              <Mail size={20} />
              <span>{settings.contactEmail}</span>
            </a>
            
            <a 
              href={`tel:${settings.contactPhone.replace(/[^0-9+]/g, '')}`}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '12px',
                color: 'var(--brand-primary)',
                textDecoration: 'none',
              transition: 'opacity 0.3s'
            }}
            onMouseOver={(e) => e.currentTarget.style.opacity = '0.8'}
            onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
          >
            <Phone size={20} />
            <span>{settings.contactPhone}</span>
          </a>
        </div>
        )}
      </section>

      <section className="demo-form-section">
        <div className="demo-content" style={{ gridTemplateColumns: '1fr', maxWidth: '800px' }}>
          <div className="demo-form">
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="body-small">
                  <User size={18} /> Full Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder="John Doe"
                />
              </div>

              <div className="form-group">
                <label className="body-small">
                  <Mail size={18} /> Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="john@company.com"
                />
              </div>

              <div className="form-group">
                <label className="body-small">
                  <MessageSquare size={18} /> Subject *
                </label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  placeholder="How can we help?"
                />
              </div>

              <div className="form-group">
                <label className="body-small">Message *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="6"
                  placeholder="Tell us more about your inquiry..."
                ></textarea>
              </div>

              {error && (
                <div style={{ 
                  padding: '16px', 
                  background: 'rgba(255, 0, 0, 0.1)', 
                  border: '1px solid rgba(255, 0, 0, 0.3)',
                  marginTop: '20px',
                  color: '#ff6b6b'
                }}>
                  {error}
                </div>
              )}

              <button 
                type="submit" 
                className="btn-primary" 
                style={{ width: '100%', marginTop: '32px' }}
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
